import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../lang";
import HGWSymbol from "./HGWSymbol";
import MessageText from "./chat/MessageText";
import EscalationPanel from "./chat/EscalationPanel";
import { CHAT_COPY } from "./chat/chatCopy";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "hgw_chat_session"; // anonymous chat session id (non-sensitive)

const msg = (role, text, extra = {}) => ({ id: crypto.randomUUID(), role, text, ...extra });

export default function ChatWidget() {
  const { lang } = useLang();
  const t = CHAT_COPY[lang] || CHAT_COPY.it;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || null);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const listRef = useRef(null);

  // Seed the welcome message the first time the panel opens
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([msg("assistant", t.welcome)]);
    }
  }, [open, messages.length, t.welcome]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, loading]);

  const sendMessage = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || sending) return;
    setMessages((m) => [...m, msg("user", text)]);
    setInput("");
    setLoading(true);
    setSending(true);
    const replyId = crypto.randomUUID();
    const setReply = (txt, extra = {}) =>
      setMessages((m) => {
        const rest = m.filter((x) => x.id !== replyId);
        return [...rest, { id: replyId, role: "assistant", text: txt, ...extra }];
      });
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text, lang }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let acc = "";
      let final = null;
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const chunks = buf.split("\n\n");
        buf = chunks.pop();
        for (const c of chunks) {
          if (!c.startsWith("data: ")) continue;
          const ev = JSON.parse(c.slice(6));
          if (ev.error) throw new Error("stream error");
          if (ev.delta) {
            acc += ev.delta;
            setLoading(false);
            setReply(acc.replace("[ESCALATE]", "").replace(/\*\*/g, ""));
          }
          if (ev.done) final = ev;
        }
      }
      if (!final) throw new Error("incomplete stream");
      if (final.session_id && final.session_id !== sessionId) {
        setSessionId(final.session_id);
        localStorage.setItem(SESSION_KEY, final.session_id);
      }
      setReply(final.reply);
      if (final.escalate) setTimeout(() => setEscalateOpen(true), 600);
    } catch (err) {
      console.error("[chat] message failed:", err);
      setReply(t.errorGeneric, { error: true });
    } finally {
      setLoading(false);
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating trigger */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="chat-trigger"
            initial={{ opacity: 0, scale: 0.85, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85 }}
            transition={{ duration: 0.35, ease: [0.19, 1, 0.22, 1] }}
            onClick={() => setOpen(true)}
            aria-label={t.open}
            data-testid="chat-trigger"
            className="fixed bottom-5 right-5 md:bottom-6 md:right-6 z-[60] group flex items-center gap-1.5 pl-2.5 pr-3.5 py-2 rounded-full bg-[#1D3F94] text-white shadow-[0_16px_40px_-12px_rgba(29,63,148,0.55)] hover:shadow-[0_20px_50px_-10px_rgba(29,63,148,0.7)] transition-all duration-300 hover:-translate-y-0.5"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inset-0 rounded-full bg-white/70 animate-ping" />
              <span className="relative rounded-full h-2 w-2 bg-white" />
            </span>
            <HGWSymbol size={16} className="text-white" />
            <span className="text-[13px] font-medium tracking-tight">{t.open}</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.19, 1, 0.22, 1] }}
            data-testid="chat-panel"
            className="fixed bottom-5 right-5 md:bottom-6 md:right-6 z-[60] w-[calc(100vw-2.5rem)] max-w-[380px] h-[560px] max-h-[calc(100vh-5rem)] bg-white rounded-3xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.25)] flex flex-col overflow-hidden border border-black/5"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 bg-[#1D3F94] text-white shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
                  <HGWSymbol size={18} className="text-white" />
                </div>
                <div>
                  <div className="text-[15px] font-semibold leading-none">{t.title}</div>
                  <div className="text-[11px] text-white/70 mt-1 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#4ade80]" />
                    {t.subtitle}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="close"
                data-testid="chat-close"
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* Body */}
            {!escalateOpen && (
              <>
                <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#f5f5f7]" data-testid="chat-messages">
                  {messages.map((m) => (
                    <div
                      key={m.id}
                      className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-[14px] leading-relaxed whitespace-pre-wrap ${
                          m.role === "user"
                            ? "bg-[#1D3F94] text-white rounded-br-sm"
                            : m.error
                            ? "bg-red-50 text-red-800 border border-red-100 rounded-bl-sm"
                            : "bg-white text-[#1d1d1f] border border-black/5 rounded-bl-sm"
                        }`}
                      >
                        <MessageText text={m.text} />
                      </div>
                    </div>
                  ))}
                  {/* Quick-reply chips: shown only when there's just the welcome message */}
                  {messages.length === 1 && messages[0].role === "assistant" && !loading && t.quickReplies && (
                    <div className="flex flex-wrap gap-2 pt-1 pl-1" data-testid="chat-quick-replies">
                      {t.quickReplies.map((q) => (
                        <button
                          type="button"
                          key={q}
                          onClick={() => sendMessage(q)}
                          data-testid={`chat-quick-${q.replace(/\s+/g, "-").toLowerCase()}`}
                          className="text-[12px] font-medium px-3 py-1.5 rounded-full bg-white text-[#1D3F94] border border-[#1D3F94]/20 hover:bg-[#1D3F94] hover:text-white hover:border-[#1D3F94] transition-colors"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  )}
                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-white border border-black/5 px-3.5 py-2.5 rounded-2xl rounded-bl-sm">
                        <div className="flex gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#1D3F94]/60 animate-bounce" style={{ animationDelay: "0ms" }} />
                          <span className="w-1.5 h-1.5 rounded-full bg-[#1D3F94]/60 animate-bounce" style={{ animationDelay: "150ms" }} />
                          <span className="w-1.5 h-1.5 rounded-full bg-[#1D3F94]/60 animate-bounce" style={{ animationDelay: "300ms" }} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Composer */}
                <div className="border-t border-black/5 bg-white px-3 py-3 shrink-0">
                  <div className="flex items-end gap-2">
                    <textarea
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={onKey}
                      rows={1}
                      placeholder={t.placeholder}
                      data-testid="chat-input"
                      disabled={sending}
                      className="flex-1 bg-[#f5f5f7] rounded-xl px-3.5 py-2.5 text-[14px] text-[#1d1d1f] placeholder:text-[#6e6e73] outline-none focus:ring-2 focus:ring-[#1D3F94]/30 resize-none max-h-24"
                    />
                    <button
                      type="button"
                      onClick={() => sendMessage()}
                      disabled={!input.trim() || sending}
                      aria-label={t.send}
                      data-testid="chat-send"
                      className="shrink-0 w-10 h-10 rounded-full bg-[#1D3F94] text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#152f6f] transition-colors"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                      </svg>
                    </button>
                  </div>
                  <div className="mt-2 flex items-center justify-between px-1">
                    <button
                      type="button"
                      onClick={() => setEscalateOpen(true)}
                      data-testid="chat-escalate-open"
                      className="text-[11px] text-[#1D3F94] font-medium hover:underline"
                    >
                      {t.escalateTitle}
                    </button>
                    <span className="text-[10px] text-[#a1a1aa]">{t.powered}</span>
                  </div>
                </div>
              </>
            )}

            {/* Escalation view */}
            {escalateOpen && (
              <EscalationPanel
                lang={lang}
                t={t}
                sessionId={sessionId}
                onClose={() => setEscalateOpen(false)}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
